import React, { ReactElement } from 'react';
import styled from 'styled-components';
import { History } from 'history';
import { useStripe, useElements } from '@stripe/react-stripe-js';
import { StripeElements, Stripe } from '@stripe/stripe-js';
import { Div, HorizontalView, VerticalView } from 'vendor-deprecated/Wrapper';
import { ICourse, IUser, ICourseLesson, IRate } from 'types/types';
import { AddToCart } from './parts/AddToCart';
import { Syllabus } from './parts/Syllabus';
import { AnimationView } from 'vendor-deprecated/Animation';
import { BaseComponent } from 'components/common/components/BaseComponent';
import { IError } from 'types/Models';
import { useAppSetting } from 'helpers/useAppSetting';
import { CheckoutModal } from './parts/CheckoutModal';
import { CourseDescription } from './parts/CourseDescription';
import { AppColors } from 'helpers';
import { AuthorAbout } from './parts/AuthorAbout';
import { RateComponent } from 'vendor-deprecated/Rate';
import { ReviewList } from 'components/common/components/Review/ReviewList';
import { BannerDownloadApp } from 'components/common/components/Banner/BannerDownloadApp';

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100vw;
`;

const CourseDescriptionWrapper = styled.div`
  width: 100%;
  background-image: ${AppColors.GRADIENT_BLUE};
  color: ${AppColors.WHITE};
  padding: 10px;
  padding-bottom: 20px;
`;

type CourseProps = {
  auth?: IUser;
  isValidReview: boolean;
  loading?: boolean;
  addCourseFreeLoading?: boolean;
  isCheckout: boolean;
  error?: IError;
  history: History;
  course: ICourse;
  onAddToCart: () => void;
  onBuyNow: () => void;
  closeCheckout: () => void;
  onAddToMyCourse: () => void;
  onEditReview: (id: string, rate: number, content: string) => void;
  onCreateReview: (rate: number, content: string) => void;
};

export const CourseDetailsComponent = React.memo((props: CourseProps) => {
  const { appLayout } = useAppSetting();
  const [bottomAddToCart, setBottomAddTCart] = React.useState<null | ReactElement>(null);
  const { course } = props;
  const stripe: Stripe | null = useStripe();
  const elements: StripeElements | null = useElements();

  return (
    <BaseComponent documentTitle={course.title} error={props.error} fullLoading={props.loading}>
      <AnimationView width={'100%'}>
        <BannerDownloadApp />
        <ContentWrapper>
          <CourseDescriptionWrapper>
            <VerticalView
              fullWidth
              height={'100%'}
              alignContent={'center'}
              paddingTop={appLayout.isMobile ? '10px' : '40px'}
            >
              <HorizontalView fullWidth maxWidth={appLayout.isMobile ? '700px' : '1200px'} alignContent={'topCenter'}>
                <Div width={`calc(100% - ${appLayout.isMobile ? '0px' : '360px'})`}>
                  <CourseDescription course={course} />
                </Div>
                <Div
                  width={appLayout.isMobile ? '100%' : '360px'}
                  alignContent={'topCenter'}
                  paddingTop={appLayout.isMobile ? '20px' : '0px'}
                  position={'relative'}
                  paddingLeft={'0px'}
                >
                  <AddToCart
                    type={'top'}
                    addCourseFreeLoading={props.addCourseFreeLoading}
                    disabled={props.auth?.role === 'AUTHOR'}
                    isAdded={true}
                    course={course}
                    onAddToCart={props.onAddToCart}
                    onBuyNow={props.onBuyNow}
                    onAddToMyCourse={props.onAddToMyCourse}
                    onShowBottom={(card) => {
                      setBottomAddTCart(card);
                    }}
                  />
                </Div>
              </HorizontalView>
              <Div />
            </VerticalView>
          </CourseDescriptionWrapper>
          <Div fullWidth alignContent={'topCenter'} paddingTop={'20px'}>
            <VerticalView fullWidth maxWidth={appLayout.isMobile ? '700px' : '1200px'}>
              <HorizontalView fullWidth alignContent={'bottomCenter'}>
                <VerticalView width={appLayout.isMobile ? '100%' : `calc(100% - 360px)`} padding={'20px'}>
                  <Div fullWidth>
                    {/* Syllabus */}
                    <Syllabus course={props.course} lessons={(course.lessons || []) as ICourseLesson[]} />
                  </Div>

                  {appLayout.isMobile && (
                    <Div fullWidth borderRadius={'4px'} overflow={'hidden'} marginBottom={'20px'}>
                      <CourseDescriptionWrapper>
                        <VerticalView fullWidth height={'100%'} alignContent={'center'}>
                          <HorizontalView fullWidth alignContent={'topCenter'}>
                            <Div fullWidth>
                              <CourseDescription hideCover course={course} />
                            </Div>
                            <Div fullWidth paddingTop={'20px'} position={'relative'} paddingLeft={'0px'}>
                              <AddToCart
                                type={'top'}
                                addCourseFreeLoading={props.addCourseFreeLoading}
                                disabled={props.auth?.role === 'AUTHOR'}
                                isAdded={true}
                                course={course}
                                onAddToCart={props.onAddToCart}
                                onBuyNow={props.onBuyNow}
                                onAddToMyCourse={props.onAddToMyCourse}
                                onShowBottom={(card) => {
                                  setBottomAddTCart(card);
                                }}
                              />
                            </Div>
                          </HorizontalView>
                          <Div />
                        </VerticalView>
                      </CourseDescriptionWrapper>
                    </Div>
                  )}

                  <Div fullWidth alignContent={'centerLeft'}>
                    {/* About Author */}
                    <AuthorAbout course={props.course} />
                  </Div>

                  {props.course.owner && (
                    <Div fullWidth alignContent={'centerLeft'} marginTop={'20px'}>
                      {/* ReviewList */}
                      <RateComponent
                        totalReviews={props.course.owner.totalReviews || 0}
                        avgRate={props.course.owner.avgRate || 0}
                        rates={(props.course.owner.rates || []) as IRate[]}
                      />
                    </Div>
                  )}
                  {props.course.id && (
                    <Div fullWidth alignContent={'centerLeft'} marginTop={'20px'}>
                      {/* ReviewList */}
                      <ReviewList
                        isValidReview={props.isValidReview}
                        auth={props.auth}
                        createdBy={props.course.createdBy}
                        courseId={props.course.id}
                        onEditReview={props.onEditReview}
                        onCreateReview={props.onCreateReview}
                      />
                    </Div>
                  )}
                </VerticalView>

                {!appLayout.isMobile && (
                  <Div position={'relative'} width={'360px'} paddingBottom={'20px'} alignContent={'centerLeft'}>
                    {bottomAddToCart}
                  </Div>
                )}
              </HorizontalView>
            </VerticalView>
          </Div>
        </ContentWrapper>
        <CheckoutModal open={props.isCheckout} elements={elements} stripe={stripe} onClose={props.closeCheckout} />
      </AnimationView>
    </BaseComponent>
  );
});
